<template>
  <b-modal id="modal-center" title="" :visible="studentIdentityOpen" @hidden="closeModal" centered size="lg" cancel-title="Close">
    <template #modal-header>
      <div class="d-flex align-items-center justify-content-between w-100">
        <span class="font-weight-bold" style="font-size: 1.25rem">Identity Card</span>

        <div class="d-flex align-items-center" style="gap: 0.625rem">
          <b-badge v-if="identityData.processingAt" pill class="profile-badge mr-25" variant="light-warning">
            Processed on {{ momentTz(identityData.processingAt).format('L @ LT') }}
          </b-badge>
          <b-badge v-if="identityData.verifiedAt" pill class="profile-badge mr-25" :variant="identityVariant(identityData.verified)">
            {{ title(identityData.verified || '') }} on {{ momentTz(identityData.verifiedAt).format('L @ LT') }}
          </b-badge>
          <b-badge pill class="profile-badge mr-25 text-capitalize" :variant="`light-info`">
            {{ title(identityData.metadata.type) || 'N/A' }}
          </b-badge>
        </div>
      </div>
    </template>

    <div class="d-flex" style="gap: 1.5rem">
      <b-row style="flex-shrink: 0; width: 40%" class="m-0">
        <b-col cols="12" md="12" lg="12" class="p-0">
          <div class="d-flex flex-column" style="gap: 1rem">
            <b-img
              v-for="document in identityData.documentIds"
              :key="document.id"
              class="w-100 overflow-hidden"
              style="border-radius: 0.4rem"
              :src="document.src"
              @error="handleThumbnailError"
            />
          </div>
        </b-col>
      </b-row>

      <b-row class="overflow-hidden m-0" style="height: min-content">
        <b-col cols="12" md="12" lg="12" class="d-flex flex-row justify-content-between card card-bg p-50" style="height: fit-content">
          <span class="d-flex text-muted">Full Name:</span>
          <span>{{ identityData.metadata.firstName }} {{ identityData.metadata.lastName }}</span>
        </b-col>

        <b-col cols="12" md="12" lg="12" class="d-flex flex-row justify-content-between card card-bg p-50" style="height: fit-content">
          <span class="d-flex text-muted">Date of Birth:</span>
          <span>{{ identityData.metadata.dob || 'N/A' }}</span>
        </b-col>

        <b-col cols="12" md="12" lg="12" class="d-flex flex-row justify-content-between card card-bg p-50" style="height: fit-content">
          <span class="d-flex text-muted">Address:</span>
          <span>{{ identityData.metadata.address.line1 || 'N/A' }}, {{ identityData.metadata.address.line2 || 'N/A' }}</span>
        </b-col>

        <b-col cols="12" md="12" lg="12" class="d-flex flex-row justify-content-between card card-bg p-50" style="height: fit-content">
          <span class="d-flex text-muted">City/State:</span>
          <span>{{ identityData.metadata.address.city || 'N/A' }}, {{ identityData.metadata.address.state || 'N/A' }}</span>
        </b-col>

        <b-col cols="12" md="12" lg="12" class="d-flex flex-row justify-content-between card card-bg p-50" style="height: fit-content">
          <span class="d-flex text-muted">Postal Code:</span>
          <span>{{ identityData.metadata.address.postal_code || 'N/A' }}</span>
        </b-col>

        <div class="w-100 m-0 d-flex" style="gap: 0.75rem">
          <div class="d-flex flex-row justify-content-between card card-bg p-50 w-50" style="height: fit-content">
            <span class="d-flex text-muted">Issued At:</span>
            <span class="text-capitalize">{{ momentTz(identityData.metadata.issuedAt).format('L') }}</span>
          </div>

          <div class="d-flex flex-row justify-content-between card card-bg p-50 w-50" style="height: fit-content">
            <span class="d-flex text-muted">Expires At:</span>
            <span class="text-capitalize">{{
              identityData.metadata.expiresAt ? momentTz(identityData.metadata.expiresAt).format('L') : 'N/A'
            }}</span>
          </div>
        </div>
      </b-row>
    </div>

    <template #modal-footer>
      <div class="d-flex justify-content-end w-100" style="gap: 0.5rem">
        <b-button variant="primary" @click="closeModal">Close</b-button>
        <b-button variant="info" @click="downloadReport">Download Report</b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BRow, BCol, BButton, BModal, VBModal, BBadge, BImg } from 'bootstrap-vue';
import { momentTz } from '@/libs/utils/time';
import { title } from '@core/utils/filter';
import ToastificationContent from '@core/components/toastification/ToastificationContent';
import fileDownload from 'js-file-download';
import store from '@/store';

export default {
  components: {
    BImg,
    BRow,
    BCol,
    BBadge,
    BButton,
    ToastificationContent,
    BModal,
    title,
  },
  directives: { 'b-modal': VBModal },
  props: {
    identityData: {
      type: Object,
      required: true,
    },
    studentIdentityOpen: {
      type: Boolean,
      required: true,
    },
  },
  methods: {
    handleThumbnailError(event) {
      event.target.src = this.identityData.documentIds[0].thumbnailUrl;
      event.target.onerror = null;
    },
    async downloadReport() {
      if (!this.identityData.reportId) return;

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Please wait a moment...',
          icon: 'DownloadIcon',
          variant: 'success',
          text: 'Preparing report for download...',
        },
      });

      try {
        const response = await store.dispatch('studentStoreModule/documentStudentDocumentPreview', {
          studentId: this.identityData.reportId.student,
          documentId: this.identityData.reportId.id,
        });

        const res = await fetch(response.data.url);
        const blob = await res.blob();
        const filename = this.identityData.reportId.name;

        fileDownload(blob, filename);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Download Complete!',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Successfully downloaded the identity report.',
          },
        });
      } catch (error) {
        console.error('Failed to download report:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Download Failed',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || error.message || 'An unknown error occurred.',
          },
        });
      }
    },
    async previewDocument(document, index) {
      await store
        .dispatch('studentStoreModule/documentStudentDocumentPreview', {
          studentId: document.student,
          documentId: document.id,
        })
        .then((response) => {
          this.$set(this.identityData.documentIds[index], 'src', response.data.url);
        })
        .catch((error) => {
          console.log('Failed to download student document', error);
          this.disableDownload = false;

          if (error.response) {
            const { response } = error;
            return this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Failed to download file.',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: response.data.message || 'An unknown error has occurred.',
              },
            });
          }

          return this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to download file.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error.message,
            },
          });
        });
    },
    closeModal() {
      this.busy = false;
      this.$emit('update:student-identity-open', false);
    },
    identityVariant(verified) {
      switch (verified) {
        case 'verified':
          return 'light-success';
        case 'requested':
          return 'light-secondary';
        case 'processing':
          return 'light-info';
        case 'action_required':
          return 'light-warning';
        case 'unverified':
        case 'failed':
          return 'light-danger';
        default:
          return 'light-warning';
      }
    },
  },
  mounted() {
    if (this.studentIdentityOpen && this.identityData?.documentIds?.length > 0) {
      this.identityData.documentIds.forEach((document, index) => {
        this.previewDocument(document, index);
      });
    }
  },
  setup() {
    return {
      title,
      momentTz,
    };
  },
};
</script>
